import { useState } from 'react';

import { useNavigate } from '@tanstack/react-router';

import { useIntervalCommentsDrawerStore } from '@/hooks/intervalComments/useIntervalCommentsDrawerStore';
import { useNotifications } from '@/hooks/useNotifications';
import { useMarkNotificationAsRead } from '@/hooks/useNotifications/useMarkNotificationAsRead';
import { useENPSModal } from '@/modules/Feedback/useENPSModal';

import { NotificationType } from './notifications.types';

export function useNotificationsAdapter() {
  const [opened, setOpened] = useState(false);
  const { data, isLoading } = useNotifications();
  const { mutate: markAsRead } = useMarkNotificationAsRead();
  const navigate = useNavigate();
  const { openENPSModal } = useENPSModal();
  const openCommentsDrawer = useIntervalCommentsDrawerStore((s) => s.open);

  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unread_count ?? 0;

  const handleNotificationClick = (n: NotificationType) => {
    markAsRead(n.id);
    setOpened(false);

    if (
      n.type === 'TimesheetApprovalNotifier::Notification' ||
      n.type === 'TimesheetRejectionNotifier::Notification'
    ) {
      navigate({
        to: '/app/timesheet',
        search: { week: n.params.timesheet?.work_date },
      });
      return;
    }

    if (n.type === 'TimesheetReminderNotifier::Notification') {
      navigate({
        to: '/app/timesheet',
        search: { week: n.params.week_ending },
      });
      return;
    }

    if (n.type === 'TaskNotifier::Notification') {
      navigate({ to: '/app/dashboard' });
      return;
    }

    if (n.type === 'FeedbackNotifier::Notification') {
      openENPSModal();
      return;
    }

    if (
      n.type === 'ProjectStartReminderNotifier::Notification' ||
      n.type === 'ProjectOverdueNotifier::Notification' ||
      n.type === 'ProjectEndReminderNotifier::Notification' ||
      n.type === 'IntervalCompletionStateSubmittedNotifier::Notification'
    ) {
      if (n.params.project) {
        navigate({
          to: '/app/projects/$projectId',
          params: { projectId: String(n.params.project.id) },
          search: { tab: 'general' },
        });
      }
      return;
    }

    if (n.type === 'IntervalCommentAddedNotifier::Notification') {
      if (n.params.interval_comment?.interval_id) {
        openCommentsDrawer(n.params.interval_comment.interval_id, null);
      }
      return;
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    opened,
    onOpenChange: setOpened,
    handleNotificationClick,
  };
}
